import { NetworkService } from '@amzn/awscat-portfolio-assessment-service-client';
import { Network } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Button, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import { FunctionComponent, useEffect, useState } from 'react';
import { match,useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { FormAction, modalActionButtonTextMap } from '../../common/form-builder/types';
import { constants } from '../../common/Localization';
import AWSCATTable from '../../common/table/Table-index';
import { ColumnDefinition } from '../../common/table/types';
import { MatchParam } from '../../common/types';
import AWSUIFormBuilder, { MetadataItem } from '../common/AWSUIFormBuilder';
import { networkColumnDefinition, networkMetadata } from './NetworkItems';

type NetworkProps = FlashContextInterface;

const networkService = new NetworkService();

const NetworkPage: FunctionComponent<NetworkProps> = ({flash}): JSX.Element => {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });

    const portfolioId = match?.params?.portfolioId;

    const metadata: MetadataItem<Network>[] = networkMetadata;
    
    const defaultNetwork: Network = {
        sizeOfPipeMB: 1,
        redundantPipe: 'true',
        ratio: 3,
        internetEgress: 0,
        excludeFromTco: [],
    };

    const [ networkItems, setNetworkItems ] = useState<Network[]>([]);
    const [ selectedNetwork, setSelectedNetwork ] = useState<Network | null>(null);
    const [ modalVisible, setModalVisible ] = useState(false);
    const [ modalHeader, setModalHeader ] = useState('Add Network');
    const [ modalNetworkItem, setModalNetworkItem ] = useState<Network>(defaultNetwork);
    const [ formAction, setFormAction ] = useState(FormAction.ADD);
    const [ loading, setLoading ] = useState(false);

    const isMounted = useIsMounted();

    async function getNetwork() {
        setLoading(true);
        try {
            if (portfolioId) {
                const network: Network[] = await networkService.get(portfolioId);
                if (isMounted()) {
                    setNetworkItems(network);
                }
            }
        } catch(err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.NETWORK_ERROR_LOADING);
                setNetworkItems([]);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    }
    useEffect(() => {
        getNetwork();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portfolioId]);

    const columnDefinition: ColumnDefinition<Network>[] = networkColumnDefinition;

    const updateSelectedNetwork = (networks: readonly Network[] | undefined) => {
        const network = networks?.length ? networks[0] : null;
        setSelectedNetwork(network);
    };

    const onAddNetworkButtonClick = (): void => {
        setModalHeader(constants.NETWORK_ADD_MODAL_HEADER);
        setModalNetworkItem(defaultNetwork);
        setFormAction(FormAction.ADD);
        setModalVisible(true);
    };
    
    const onEditNetworkButtonClick = (): void => {
        setModalHeader(constants.NETWORK_EDIT_MODAL_HEADER);
        if (selectedNetwork) {
            setModalNetworkItem(selectedNetwork);
        }
        setFormAction(FormAction.EDIT);
        setModalVisible(true);
    };

    const onDeleteNetworkButtonClick = (): void => {
        setModalHeader(constants.NETWORK_DELETE_MODAL_HEADER);
        if (selectedNetwork) {
            setModalNetworkItem(selectedNetwork);
        }
        setFormAction(FormAction.DELETE);
        setModalVisible(true);
    };

    const onFormSubmit = async (item: Network) => {
        setLoading(true);
        if (portfolioId) {
            const updatedNetwork = networkItems;
            if (formAction === FormAction.ADD) {
                updatedNetwork.push(item);
            }
            else {
                const index = updatedNetwork.findIndex(network => network === selectedNetwork);
                if (formAction === FormAction.DELETE) {
                    updatedNetwork.splice(index, 1);
                }
                else {
                    updatedNetwork[index] = {...item};
                }
            }
            await networkService.create(portfolioId, updatedNetwork);
        }
        await getNetwork();
        setModalVisible(false);
    };

    const modalSize = formAction === FormAction.DELETE ? 'medium' : 'small';
    const deleteErrorText = (
        <span>
            <h3 className='awscatui-mt-n'>{constants.FORM_DELETE_ERROR_TITLE}</h3>
            <p>{`${constants.FORM_DELETE_ERROR_CONTENT} ${constants.NETWORK}. ${constants.FORM_DELETE_ERROR_PROCEED}`}</p>
        </span>
    );

    return (
        <span>
            <AWSCATTable
                resourceName={constants.NETWORK_TABLE_HEADER} 
                items={networkItems}
                columnDefinitions={columnDefinition}
                loading={loading}
                selectionType='single'
                autoGenerateVisibleContent={true}
                headerActions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button disabled={loading || !selectedNetwork} onClick={onEditNetworkButtonClick}>{constants.EDIT_BUTTON_TEXT}</Button>
                        <Button disabled={loading || !selectedNetwork} onClick={onDeleteNetworkButtonClick}>{constants.DELETE_BUTTON_TEXT}</Button>
                        <Button disabled={loading} onClick={onAddNetworkButtonClick} variant="primary">{constants.NETWORK_ADD_BUTTON_TEXT}</Button>
                    </SpaceBetween>
                }
                onSelectionChange={updateSelectedNetwork}
                useDefaultDownload
            />
            <Modal size={modalSize} header={modalHeader} visible={modalVisible} onDismiss={() => setModalVisible(false)}>
                <AWSUIFormBuilder
                    item={modalNetworkItem}
                    metadata={metadata}
                    loading={loading}
                    readonly={formAction === FormAction.DELETE}
                    formDeleteMessage={formAction === FormAction.DELETE ? deleteErrorText : undefined}
                    primaryAction={modalActionButtonTextMap.get(formAction)}
                    onCancel={() => setModalVisible(false)}
                    onSubmit={onFormSubmit}
                />
            </Modal>
        </span>
    );
};

export default withFlashContext(NetworkPage);
